import { useQuery } from "@tanstack/react-query";
import { api, API_BASE } from "@/lib/api";
import { useLang } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { FileText, Download, TrendingUp, BookOpen, Wrench } from "lucide-react";

const ICON_MAP = {
  "umsatz-guide": TrendingUp,
  "nutzer-handbuch": BookOpen,
  "entwickler-handbuch": Wrench,
};

const DESCRIPTIONS = {
  de: {
    "umsatz-guide":
      "Strategie & konkrete Hebel: Wie du mit Rankly in deinen 4 Shops mehr Umsatz generierst. 12-Wochen-Roadmap inklusive.",
    "nutzer-handbuch":
      "Vollständige Anleitung mit allen Modulen (Dashboard, Keywords, Site Audit, CRO Studio, Reports).",
    "entwickler-handbuch":
      "Für Entwickler: Architektur, API-Referenz, MongoDB-Schema, Deployment auf VPS, Wartung.",
  },
  en: {
    "umsatz-guide":
      "Strategy & concrete levers: how to use Rankly to generate more revenue across your 4 shops. 12-week roadmap included.",
    "nutzer-handbuch":
      "Complete guide covering all modules (Dashboard, Keywords, Site Audit, CRO Studio, Reports).",
    "entwickler-handbuch":
      "For developers: architecture, API reference, MongoDB schema, VPS deployment, maintenance.",
  },
};

export default function DocsDownloadsSection() {
  const { t, lang } = useLang();

  const { data: docs = [], isLoading } = useQuery({
    queryKey: ["docs"],
    queryFn: async () => (await api.get("/docs")).data,
  });

  if (isLoading) {
    return <div className="text-sm text-muted-foreground font-mono">Loading…</div>;
  }

  const descriptions = DESCRIPTIONS[lang] || DESCRIPTIONS.de;

  return (
    <div>
      <p className="text-xs text-muted-foreground font-mono mb-4">
        {t("docs_description")}
      </p>
      <ul className="grid gap-3 md:grid-cols-1">
        {docs.map((d) => {
          const Icon = ICON_MAP[d.id] || FileText;
          const downloadUrl = `${API_BASE}/docs/${d.id}`;
          return (
            <li
              key={d.id}
              data-testid={`doc-row-${d.id}`}
              className="flex items-start justify-between gap-4 border border-border rounded-md p-4 hover:bg-muted/40 transition-colors"
            >
              <div className="flex items-start gap-3 min-w-0">
                <div className="mt-0.5 h-9 w-9 shrink-0 rounded-md bg-foreground text-background grid place-items-center">
                  <Icon className="h-4 w-4" />
                </div>
                <div className="min-w-0">
                  <div className="font-medium text-sm">
                    {d.title.replace(/\.pdf$/, "")}
                  </div>
                  <div className="text-xs text-muted-foreground mt-1 leading-relaxed">
                    {descriptions[d.id]}
                  </div>
                </div>
              </div>
              <a
                href={downloadUrl}
                download
                data-testid={`doc-download-${d.id}`}
                className="shrink-0"
              >
                <Button size="sm" variant="outline" className="gap-1.5">
                  <Download className="h-3.5 w-3.5" />
                  PDF
                </Button>
              </a>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
